import React, { useEffect, useMemo, useState } from 'react';
import '../../../../../../../Stylesheets/Dashboard/Tabs/Expenses/AddExpenseModal.css';

const CATEGORIES = ['Labor','Material','Transport','Equipment','Subcontractor','Permit','Misc','Other'];
const METHODS = ['UPI','Cash','Bank','Card','Other'];
const MAX_NOTES = 200;
const MAX_BILL_NO = 32;

const toYMD = (d) => {
  if (!d) return '';
  const dt = typeof d === 'string' ? new Date(d) : d;
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const todayISO = () => new Date().toISOString().slice(0, 10);

const EditExpenseModal = ({ show = true, expense, onClose, onSubmit, submitting = false }) => {
  // Prefill from expense
  const [category, setCategory] = useState('');
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [date, setDate] = useState(todayISO());
  const [paymentMethod, setPaymentMethod] = useState('Other');
  const [billNumber, setBillNumber] = useState('');

  useEffect(() => {
    if (!expense) return;
    setCategory(expense.category || '');
    setAmount(String(expense.amount ?? ''));
    setNotes(expense.notes || '');
    setDate(toYMD(expense.date) || todayISO());
    setPaymentMethod(expense.paymentMethod || 'Other');
    setBillNumber(expense.bill?.billNumber || expense.billNumber || '');
  }, [expense]);

  const [touched, setTouched] = useState({});

  const errors = useMemo(() => {
    const errs = {};
    if (!category || !CATEGORIES.includes(category)) errs.category = 'Select a valid category.';
    const num = Number(amount);
    if (!isFinite(num) || num <= 0) errs.amount = 'Amount must be greater than 0.';
    if (!date) errs.date = 'Date is required.';
    else if (date > todayISO()) errs.date = 'Date cannot be in the future.';
    if (!paymentMethod || !METHODS.includes(paymentMethod)) errs.paymentMethod = 'Select a payment method.';
    if (notes && notes.length > MAX_NOTES) errs.notes = `Notes ≤ ${MAX_NOTES} characters.`;
    if (billNumber && billNumber.length > MAX_BILL_NO) errs.billNumber = `Bill number ≤ ${MAX_BILL_NO} characters.`;
    return errs;
  }, [category, amount, date, paymentMethod, notes, billNumber]);

  const isValid = Object.keys(errors).length === 0;

  const blockInvalidNumberKeys = (e) => {
    if (['e','E','+','-'].includes(e.key)) e.preventDefault();
  };

  const handleSubmit = () => {
    if (!isValid) {
      setTouched({ category: true, amount: true, date: true, paymentMethod: true, notes: true, billNumber: true });
      return;
    }

    const updates = {
      category,
      amount: Number(amount),
      notes: notes.trim(),
      date,
      paymentMethod,
    };

    // only include billNumber if present (your edit controller can accept it or ignore)
    if (billNumber.trim()) updates.billNumber = billNumber.trim();

    onSubmit({ expenseId: expense._id, updates });
    onClose();
  };

  if (!show || !expense) return null;
 
  return (
    <div className="modal-overlay">
      <div className="modal animated" role="dialog" aria-modal="true" aria-labelledby="edit-expense-title">
        <h2 id="edit-expense-title">Edit Expense</h2>

         <div className="modal-body">

        <label className="field">
          <span>Category</span>
          <select value={category} onChange={(e) => setCategory(e.target.value)} onBlur={() => setTouched(t => ({...t, category: true}))}>
            <option value="">Select Category</option>
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          {touched.category && errors.category && <div className="error">{errors.category}</div>}
        </label>

        <label className="field">
          <span>Amount</span>
          <input
            type="number"
            inputMode="decimal"
            value={amount}
            onKeyDown={blockInvalidNumberKeys}
            onChange={(e) => setAmount(e.target.value)}
            onBlur={() => setTouched(t => ({...t, amount: true}))}
            min="0" step="0.01"
          />
          {touched.amount && errors.amount && <div className="error">{errors.amount}</div>}
        </label>

        <label className="field">
          <span>Notes <em className="muted">(optional)</em></span>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={MAX_NOTES} />
          <div className="help">{notes.length}/{MAX_NOTES}</div>
          {touched.notes && errors.notes && <div className="error">{errors.notes}</div>}
        </label>

        <div className="row-2">
          <label className="field">
            <span>Date</span>
            <input type="date" value={date} max={todayISO()} onChange={(e) => setDate(e.target.value)} onBlur={() => setTouched(t => ({...t, date: true}))}/>
            {touched.date && errors.date && <div className="error">{errors.date}</div>}
          </label>

          <label className="field">
            <span>Payment Method</span>
            <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} onBlur={() => setTouched(t => ({...t, paymentMethod: true}))}>
              {METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
            {touched.paymentMethod && errors.paymentMethod && <div className="error">{errors.paymentMethod}</div>}
          </label>
        </div>

        <label className="field">
          <span>Bill Number <em className="muted">(optional)</em></span>
          <input type="text" value={billNumber} onChange={(e) => setBillNumber(e.target.value)} maxLength={MAX_BILL_NO}/>
          {touched.billNumber && errors.billNumber && <div className="error">{errors.billNumber}</div>}
        </label>

        </div>

        <div className="modal-actions">
          <button onClick={handleSubmit} disabled={!isValid || submitting}>{submitting ? 'Saving…' : 'Save Changes'}</button>
          <button onClick={onClose} className="cancel" disabled={submitting}>Cancel</button>
        </div>
      </div>
    </div>
  );
};

export default EditExpenseModal;
